import { defineStore } from "pinia";
import router from "../router";
import type { QuizData, Answer, Lead } from "../types";
import { dispatchQuizStartIfFresh, primeSiteSlugForEvents } from "../analytics/events";
import { waitForExternalSessionCookie, getSessionId } from "../analytics/session";
import { getVariantIdForSession } from "../analytics/variant";
import {
  captureSessionAttributes,
  __EMPTY_SESSION_ATTRIBUTES,
  type SessionAttributes,
} from "../analytics/session-attributes";
import { applyVariant } from "./variants";
import { skipUtmParams } from "../components/redirect";

const API_URLS = {
  LEADCHECK: "https://wgtk52k6dqdr5barbgeftvxcye0lqzlw.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEAD: "https://fhuezwgl4usr2pxjfxyhjlacgm0coxbh.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEADCHECK_BY_HASHED_EMAIL: "https://vu4a4euzjxlsr4cecxfxhv5efq0ilgkk.lambda-url.us-east-1.on.aws/" // new leadclump-quiz-api function urls
};

const FETCH_QUIZ_DATA_URL = (site: string, quizId: string) =>
  `https://hlkl4tw3vd.execute-api.us-east-1.amazonaws.com/prod/site/${site}/quiz/${quizId}/public`;

async function loadQuizData(site: string, quizId: string): Promise<QuizData> {
  let quizDataLocal: QuizData;

  try {
    quizDataLocal = (await import("../quiz.json")).default as unknown as QuizData;
  } catch {
    quizDataLocal = {} as QuizData;
  }

  let quizData = quizDataLocal;
  const isDevelopment = import.meta.env.MODE === "development";

  if (isDevelopment && Object.keys(quizData).length > 0) {
    quizData = quizDataLocal;
  } else {
    try {
      console.log("fetching quizData from API...")
      quizData = await fetchQuizDataFromAPI(site, quizId);
    } catch {
      quizData = {} as QuizData;
    }
  }

  if (quizData.facebookpixels && !Array.isArray(quizData.facebookpixels)) {
    quizData.facebookpixels = [quizData.facebookpixels];
  }
  if (quizData.tiktokpixels && !Array.isArray(quizData.tiktokpixels)) {
    quizData.tiktokpixels = [quizData.tiktokpixels];
  }
  if (quizData.googleads && !Array.isArray(quizData.googleads)) {
    quizData.googleads = [quizData.googleads];
  }

  if (import.meta.env.MODE === "development") {
    quizData.url = "http://localhost:5173";
  }
  return quizData;
}

async function fetchQuizDataFromAPI(site: string, quizId: string): Promise<QuizData> {
  try {
    const response = await fetch(FETCH_QUIZ_DATA_URL(site, quizId));
    if (!response.ok) {
      console.error("Error loading quiz.json from API");
      return {} as QuizData;
    }
    return await response.json();
  } catch (error) {
    console.error("Connection error when trying to load quiz.json from API:", error);
    return {} as QuizData;
  }
}

export const useMainStore = defineStore({
  id: "main",
  state: () => ({
    scriptsLoaded: false,
    redirecting: false,
    fetching: false,
    questionAnswers: [] as Answer[],
    currentIndex: 0,
    formLead: { name: "", email: "", phone: "" },
    quizData: {} as QuizData,
    isLoading: true,
    currentStep: 1,
    totalSteps: 0,
    // Quiz-mount ad-attribution snapshot for Google Data Manager API
    // sessionAttributes match-rate uplift. Populated once at App mount via
    // _captureSessionAttributes(), sent inside createLead payload at submit.
    // Default is an all-empty object so consumers never have to null-check.
    // See src/analytics/session-attributes.ts for the capture contract.
    sessionAttributes: { ...__EMPTY_SESSION_ATTRIBUTES } as SessionAttributes,
  }),
  getters: {
    currentQuestion(state) {
      return state.quizData.questions?.[state.currentIndex] ?? null;
    },
    questionsLength(state) {
      return state.quizData.questions?.length || 0;
    },
    progressBarPercentage: (state) => {
      if (!state.totalSteps) return 0;
      const percentage = Math.round((state.currentStep / state.totalSteps) * 100);
      return Math.min(percentage, 100);
    },
    isLastStep(): boolean {
      return this.currentIndex === this.questionsLength;
    },
    allQuestionsAnswered(state) {
      return (state.questionAnswers.length > 0 && state.questionAnswers.length === (state.quizData.questions?.length || 0));
    },
    quizQuestions(state) {
      return state.quizData.questions;
    },
    quizUrlLogic(state) {
      return state.quizData.url_logic;
    },
    gateData(state) {
      return state.quizData.gate;
    },
    getQuestionById(state) {
      return (question_id: string) => state.quizData.questions?.find((q) => q.id === question_id);
    },
    getLeadAnswers(state) {
      return state.questionAnswers.map((qa) => ({
        id: qa[0],
        answer: qa[1]
      }));
    },
    getLeadAnswerByQuestionId(state) {
      return (question_id: string) => state.questionAnswers.filter((qa) => qa[0] === question_id).map((qa) => qa[1]);
    },
  },
  actions: {
    /**
     * Capture the quiz-mount ad-attribution snapshot. Called once at App.vue
     * `<script setup>` BEFORE checkRoutes, so query params and DOM state are
     * the originals (before any future router guard could mutate them).
     *
     * Idempotency lives in the underlying module's `cached` (single source of
     * truth). The store action just assigns state on first call; subsequent
     * calls re-assign the same cached reference.
     *
     * Wrapped in try/catch so a module-level throw cannot break App mount.
     */
    _captureSessionAttributes(): void {
      try {
        this.sessionAttributes = captureSessionAttributes();
      } catch {
        // Leave the all-empty default in place. Payload still sends the block;
        // CH rows get empty strings for this session rather than dropped attribution.
      }
    },
    async initializeQuizData(site: string, quizId: string) {
      this.isLoading = true;
      // Run the leadclump-flow cookie wait in parallel with the quiz data fetch:
      // 1. The wait is independent of network IO, so overlapping costs nothing.
      // 2. Both must complete before we flip isLoading=false, otherwise Vue's
      //    reactive flush could mount child components and let them fire analytics
      //    (which calls getSessionId) before leadclump-flow's pblk_sid lands —
      //    locking sessionIdSource to 'generated' for the rest of the session.
      // 3. applyVariant() below ALSO depends on this order: it reads the real
      //    pblk_sid to bucket A/B/control. Calling it before the wait would
      //    lock the variant cache to 'control' for the session and silently
      //    disable B rendering. Tests in store/__tests__/variants.spec.ts
      //    pin this invariant.
      const [baseConfig] = await Promise.all([
        loadQuizData(site, quizId),
        waitForExternalSessionCookie(),
      ]);
      // Prime cachedSiteSlug BEFORE applyVariant so any variants_error event
      // emitted from a malformed override carries the right site_slug.
      // dispatchQuizStartIfFresh below also calls this internally — harmless
      // because deriveSiteSlug is pure and idempotent.
      primeSiteSlugForEvents(baseConfig);
      const variantId = getVariantIdForSession(getSessionId());
      this.quizData = applyVariant(baseConfig, variantId);
      this.isLoading = false;
      dispatchQuizStartIfFresh(this.quizData);
      this.checkRoutes();
      this.totalSteps = (this.quizData?.questions?.length) + 1 || 0;
    },
    pushQuestionAnswer(questionAnswer: Answer) {
      const isDuplicate = this.questionAnswers.some(
        ([qId, answer]) => qId === questionAnswer[0] && answer === questionAnswer[1]
      );

      if (!isDuplicate) {
        this.questionAnswers.push(questionAnswer);

        if (this.currentIndex < this.questionsLength - 1) {
          this.currentIndex += 1;
          this.currentStep = this.currentIndex + 1;
        }
      }
    },
    async apiRequest(url: string, method: string, data: unknown = null) {
      this.fetching = true;
      const options: RequestInit = {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      try {
        const response = await fetch(url, options);
        if (response.ok)
          return await response.json();
        else
          throw response
      } catch (error) {
        console.error(error);
        return error;
      } finally {
        this.fetching = false;
      }
    },
    async checkLead(email: string, site: string): Promise<unknown> {
      this.fetching = true;
      try {
        const response = await fetch(API_URLS.LEADCHECK, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, site }),
        });

        if (!response.ok) {
          throw new Error(`Request error: ${response.status} - ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Verification result:', result);
        return result;
      } catch (error) {
        console.error('Error checking lead:', error);
        throw error;
      } finally {
        this.fetching = false;
      }
    },
    isLeadCheckValid(response: { valid: boolean; safe: boolean }) {
      return response.valid && response.safe;
    },
    async createLead(lead: Lead) {
      const result = await this.apiRequest(API_URLS.LEAD, "POST", lead);
      return result;
    },
    async retrieveLeadcheckByHashedEmail() {
      const hashed_email = this._getCookie('leadclump_e_sha256');
      if (hashed_email) {
        const result = await this.apiRequest(API_URLS.LEADCHECK_BY_HASHED_EMAIL, "POST", {"hashed_email": hashed_email});
        return result;
      }
      return null;
    },
    resetQuiz() {
      this.redirecting = true;
      this.questionAnswers = [];
      this.currentIndex = 0;
    },
    redirectToLanding() {
      const quizData = this.quizData;
      const leadAnswers = this.getLeadAnswers;
      const urlLogic = this.quizUrlLogic || [];
      
      let direct_to: string | undefined = undefined;

      // Sort URLs by priority
      urlLogic.sort((a, b) => {
        if (a.priority === undefined && b.priority === undefined) return 0;
        if (a.priority === undefined) return 1;
        if (b.priority === undefined) return -1;
        return a.priority - b.priority;
      });

      // Find the first matching URL logic
      for (const url_logic of urlLogic) {
        const leadAnswerValues = leadAnswers.map((answer) => answer.answer);
        const urlLogicAnswerValues = url_logic.answers.map(answer => answer.value);

        if (!url_logic.answers || url_logic.answers.length === 0 || leadAnswerValues.every((answer) => urlLogicAnswerValues.includes(answer))) {
          direct_to = url_logic.url;

          // Handle url_variants
          if (url_logic.url_variant && url_logic.variant_weight && Number(url_logic.variant_weight) > 0 && Number(url_logic.variant_weight) < 100) {
            if (Math.random() >= (1 - (parseInt(url_logic.variant_weight.toString()) / 100))) {
              direct_to = url_logic.url_variant;
            }
          } else if (url_logic.url_variants) {
            if (Array.isArray(url_logic.url_variants) && url_logic.url_variants.length > 0 && typeof url_logic.url_variants[0] === 'string') {
              const stringVariants = url_logic.url_variants.filter((v): v is string => typeof v === 'string');
              if (stringVariants.length > 0) {
                const index = Math.floor(Math.random() * stringVariants.length);
                direct_to = stringVariants[index];
                break;
              }
            }

            const weightedVariants = url_logic.url_variants.filter((v): v is { weight: number; url: string } => typeof v === 'object' && v !== null && 'weight' in v);
            const totalVariantWeight = weightedVariants.reduce((sum, variant) => sum + variant.weight, 0);
            
            if (totalVariantWeight > 100) {
              direct_to = url_logic.url;
              break;
            }

            if (weightedVariants.length === 0) {
              direct_to = url_logic.url;
              break;
            }

            let cumulativeWeight = 0;
            const variantWeights: number[] = [];
            weightedVariants.forEach(variant => {
              cumulativeWeight += variant.weight / 100;
              variantWeights.push(cumulativeWeight);
            });
            variantWeights.push(1);

            const randomNum = Math.random();
            for (let i = 0; i < variantWeights.length; i++) {
              if (randomNum < variantWeights[i]) {
                direct_to = i < weightedVariants.length ? weightedVariants[i].url : url_logic.url;
                break;
              }
            }
          }

          if (direct_to) {
            break;
          }
        }
      }

      const finalUrl = direct_to || quizData.quiz_default_url;
      
      // Adiciona parâmetros da URL atual
      const queryParams = new URLSearchParams(window.location.search);
      const urlParams = new URLSearchParams();
      
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'sub_id'].forEach(param => {
        const value = queryParams.get(param);
        if (value) urlParams.set(param, value);
      });

      // Answer-level skip: this action only runs when an answer carries skip=true
      // (Question.vue), i.e. the user jumped straight to the landing page without
      // finishing the quiz or seeing the gate. Stamp last so it overrides the
      // inbound UTM it relabels. DEV-1418.
      Object.entries(skipUtmParams(quizData)).forEach(([param, value]) => {
        urlParams.set(param, value);
      });

      const redirect_url = `${finalUrl}?${urlParams.toString()}`;
      
      this.resetQuiz();

      if (window.location !== window.parent.location) {
        window.top!.location.href = redirect_url;
      } else {
        window.location.href = redirect_url;
      }
    },
    checkRoutes() {
      if (this.allQuestionsAnswered) {
        this.currentStep = this.totalSteps;
        router.push('/gate');
        return;
      }
      router.push({ name: 'question', query: router.currentRoute.value.query });
    },
    _getCookie(name: string): string {
      try {
        const value = document.cookie
          .split('; ')
          .find(row => row.startsWith(name + '='))
          ?.split('=')[1];
        return value ? decodeURIComponent(value) : '';
      } catch (e) {
        console.error(e);
        return '';
      }
    },
    _setCookie(name: string, value: string, days: number) {
      try {
        let expires = ""
        if (days) {
          const date = new Date()
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
          expires = "; expires=" + date.toUTCString()
        }
        // Mirror src/analytics/session.ts: cross-site embed support requires
        // SameSite=None; Secure inside iframes on HTTPS. Browsers reject
        // SameSite=None without Secure, so on http (localhost dev) fall back
        // to Lax. Keeps both cookie writers consistent so a future audit can
        // reason about both with one mental model.
        //
        // Note on TTL: Safari ITP caps document.cookie-set cookies at 7 days
        // of storage regardless of the expires attribute. So a `days=30` call
        // resolves to ~7 days on Safari/iOS — the 30 is aspirational on
        // Chromium/Firefox and a floor of 7 on WebKit. Plan capacity (e.g.
        // GAds EC match-window) accordingly.
        const isSecure = typeof location !== 'undefined' && location.protocol === 'https:'
        const sameSite = isSecure ? 'None; Secure' : 'Lax'
        // Encode the value so attacker-controlled inputs (e.g. a gclid like
        // `; path=/; domain=.evil.com`) cannot inject extra cookie attributes.
        // Symmetric with _getCookie above, which decodeURIComponent-s the read.
        // Real-world values (hex digests, Google gclids) are URL-safe and pass
        // through unchanged.
        const encoded = encodeURIComponent(value == null ? "" : String(value))
        document.cookie =
          `${name}=${encoded}${expires}; path=/; SameSite=${sameSite}`
      } catch (e) {
        console.error(e);
      }
    }
  },
});
